import React, { useEffect } from 'react';
import { useParams, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { useLanguage } from '@/lib/i18n/LanguageContext';
import { Language } from '@/lib/i18n/translations';
import { LANGUAGE_PREFIXES } from '@/lib/i18n/useLocalizedPath';
import { Header } from '@/components/Header';
import { MaintenanceBanner } from '@/components/MaintenanceBanner';
import { CartDrawer } from '@/components/CartDrawer';
import { STATIC_TRANSLATIONS, PRODUCT_SILOS } from '@/config/urlTranslations';
import { UrlTranslation } from '@/hooks/useUrlTranslations';
import { useProductSlugTranslationsData, ProductSlugTranslation } from '@/hooks/useProductSlugTranslations';

const SUPPORTED_LANGS = ['en', 'de', 'es', 'fr', 'pt', 'ru', 'it', 'sv', 'zh'];

/**
 * Find the page_key for a given slug by checking all language columns
 */
function findPageKeyBySlug(slug: string): { pageKey: string; } | null {
  const normalizedSlug = slug.startsWith('/') ? slug : `/${slug}`;
  
  for (const t of STATIC_TRANSLATIONS) {
    for (const lang of SUPPORTED_LANGS) {
      const langKey = `${lang}_slug` as keyof UrlTranslation;
      if (t[langKey] === normalizedSlug) {
        return { pageKey: t.page_key };
      }
    }
  }
  return null;
}

/**
 * Get the translated slug for a page_key in a target language
 */
function getSlugForLang(pageKey: string, targetLang: string): string {
  const t = STATIC_TRANSLATIONS.find(tr => tr.page_key === pageKey);
  if (!t) return '/';
  const langKey = `${targetLang}_slug` as keyof UrlTranslation;
  return (t[langKey] as string) || t.en_slug;
}

/**
 * Resolve a product slug (possibly localized) back to the English template slug
 */
function resolveToTemplateSlug(
  productSlugTranslations: ProductSlugTranslation[],
  slug: string
): string {
  // Check if this slug is a localized slug in any language
  const match = productSlugTranslations.find(t => t.localized_slug === slug);
  if (match) return match.template_slug;
  
  // Check if it's already a template slug
  const direct = productSlugTranslations.find(t => t.template_slug === slug);
  if (direct) return direct.template_slug;
  
  // Return as-is (unknown slug)
  return slug;
}

/**
 * Translate a product silo path between languages, including the product slug
 */
function translateProductPath(
  pathWithoutLangPrefix: string,
  targetLang: string,
  productSlugTranslations: ProductSlugTranslation[]
): string | null {
  for (const [lang, silo] of Object.entries(PRODUCT_SILOS)) {
    if (pathWithoutLangPrefix.startsWith(`/${silo}/`)) {
      const rawProductSlug = pathWithoutLangPrefix.slice(silo.length + 2); // +2 for leading and trailing /
      const targetSilo = PRODUCT_SILOS[targetLang] || PRODUCT_SILOS.en;
      
      // Resolve the product slug to English template slug, then to target language
      const templateSlug = resolveToTemplateSlug(productSlugTranslations, rawProductSlug);
      
      // Find the localized slug for the target language
      const targetTranslation = productSlugTranslations.find(
        t => t.template_slug === templateSlug && t.language === targetLang
      );
      const finalSlug = targetTranslation ? targetTranslation.localized_slug : templateSlug;
      
      return `/${targetSilo}/${finalSlug}`;
    }
  }
  return null;
}

/**
 * Router component that handles language detection from URL
 * and syncs it with the LanguageContext.
 * Also renders the persistent Header and CartDrawer so they
 * don't unmount/remount on every page navigation.
 */
export const LanguageRouter: React.FC = () => {
  const { lang } = useParams<{ lang?: string }>();
  const { language, setLanguage } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const { data: productSlugTranslations = [] } = useProductSlugTranslationsData();

  // Detect language from URL path (since route params use literal paths, not :lang)
  const detectedLangFromPath = location.pathname.match(/^\/(de|es|fr|pt|ru|it|sv|zh)(\/|$)/)?.[1] as Language | undefined;
  const effectiveLang = lang || detectedLangFromPath;

  useEffect(() => {
    if (effectiveLang && LANGUAGE_PREFIXES.includes(effectiveLang as any)) {
      setLanguage(effectiveLang as Language, false);
      return;
    }

    setLanguage('en', false);
  }, [effectiveLang, setLanguage]);

  // Handle language switch - translate URL slugs when language changes
  useEffect(() => {
    const currentLangFromUrl = detectedLangFromPath;
    
    // Guard: Don't redirect while language context is still syncing with URL
    if (effectiveLang && LANGUAGE_PREFIXES.includes(effectiveLang as any) && language !== effectiveLang) {
      return;
    }
    
    // Strip the language prefix to get the bare path
    const pathWithoutLang = location.pathname.replace(/^\/(de|es|fr|pt|ru|it|sv|zh)/, '') || '/';

    // If language is English and we have a prefix, redirect to non-prefixed
    if (language === 'en' && currentLangFromUrl) {
      // Translate the slug to English
      const match = findPageKeyBySlug(pathWithoutLang);
      if (match) {
        const enSlug = getSlugForLang(match.pageKey, 'en');
        navigate(enSlug + location.search, { replace: true });
      } else {
        // Try product path translation
        const productPath = translateProductPath(pathWithoutLang, 'en', productSlugTranslations);
        navigate((productPath || pathWithoutLang) + location.search, { replace: true });
      }
      return;
    }
    
    // If language is not English and we don't have the correct prefix
    if (language !== 'en' && currentLangFromUrl !== language) {
      // Translate the slug to the target language
      const match = findPageKeyBySlug(pathWithoutLang);
      if (match) {
        const targetSlug = getSlugForLang(match.pageKey, language);
        navigate(`/${language}${targetSlug}${location.search}`, { replace: true });
      } else {
        // Try product path translation (now with full slug translation)
        const productPath = translateProductPath(pathWithoutLang, language, productSlugTranslations);
        const finalPath = productPath || pathWithoutLang;
        navigate(`/${language}${finalPath}${location.search}`, { replace: true });
      }
    }
  }, [language, location.pathname, location.search, navigate, productSlugTranslations]);

  return (
    <MaintenanceBanner>
      <Header />
      <CartDrawer />
      <Outlet />
    </MaintenanceBanner>
  );
};